import { ReactNode } from 'react';

import ErrorBoundary from '@/components/common/ErrorBoundary';
import ErrorBoundaryDisplay from '@/components/common/ErrorBoundaryDisplay';

interface PageErrorBoundaryProps {
  source: string;
  children: ReactNode;
}

interface RouteErrorBoundaryProps {
  children: ReactNode;
}

export const PageErrorBoundary = ({ source, children }: PageErrorBoundaryProps): ReactNode => (
  <ErrorBoundary
    source={`page:${source}`}
    contextMetadata={{ page: source }}
    fallbackRender={({ error, resetError, lastErrorAt }) => (
      <ErrorBoundaryDisplay
        error={error}
        timestamp={lastErrorAt}
        onRetry={resetError}
        onHardRefresh={() => window.location.reload()}
      />
    )}
  >
    {children}
  </ErrorBoundary>
);

export const RouteErrorBoundary = ({ children }: RouteErrorBoundaryProps): ReactNode => {
  const handleRetry = () => {
    if (typeof window === 'undefined') {
      return;
    }
    if ('caches' in window) {
      void caches.keys().then(names => {
        for (const name of names) {
          void caches.delete(name);
        }
        return undefined;
      });
    }
    window.location.reload();
  };

  const handleHardRefresh = () => {
    if (typeof window === 'undefined') {
      return;
    }
    if ('caches' in window) {
      void caches.keys().then(names => {
        for (const name of names) {
          void caches.delete(name);
        }
        return undefined;
      });
    }

    // Preserve preview data before clearing storage
    const previewData: Record<string, string> = {};
    const urlParams = new URLSearchParams(window.location.search);
    const previewId = urlParams.get('preview');
    if (previewId) {
      // Check both sessionStorage and localStorage
      const data = sessionStorage.getItem(previewId) ?? localStorage.getItem(previewId);
      if (data) {
        previewData[previewId] = data;
      }
    }

    localStorage.clear();
    sessionStorage.clear();

    // Restore preview data after clearing
    if (previewId && previewData[previewId]) {
      sessionStorage.setItem(previewId, previewData[previewId]);
      localStorage.setItem(previewId, previewData[previewId]);
    }

    window.location.href = window.location.href + '?v=' + Date.now();
  };

  return (
    <ErrorBoundary
      source="RouteErrorBoundary"
      contextMetadata={{ boundary: 'RouteErrorBoundary' }}
      fallbackRender={({ error, lastErrorAt }) => (
        <ErrorBoundaryDisplay
          error={error}
          timestamp={lastErrorAt}
          onRetry={handleRetry}
          onHardRefresh={handleHardRefresh}
        />
      )}
    >
      {children}
    </ErrorBoundary>
  );
};
